import * as React from 'react';
import FileUploadIcon from "@mui/icons-material/FileUpload";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { UneditedImagesApi } from '../queries/UneditedImagesApi';
import { ImageModel, ImageUploadResponse } from '../../../shared/types';
import { ImageDropzone, ImageDropzoneLabels } from '../../../shared/view/ImageDropzone';
import { useImageDataUpdate } from '../../../shared/state/useImageDataUpdate';
import {
    DigitalStylingThumbnails,
    DigitalStylingThumbnailsLabels
} from '../../../shared/view/DigitalStylingThumbnails';
import { UploadsInProgress } from '../../../shared/view/UploadsInProgress';
import { FailedUploads } from '../../../shared/view/FailedUploads';
import { IMAGE_REFETCH_INTERVAL } from '../../../shared/state/thumbnailUtils';
import { downloadZip } from '../../../shared/state/downloadUtils';

export type CustomerOrSupportImage = {
    id: number;
    isUploadedByCustomer: boolean;
};

export type UploadUneditedImagesLabels = {
    uploadButton: string;
    failedUploadsHeading: string;
    uploadsInProgressHeading: string;
} & ImageDropzoneLabels &
    DigitalStylingThumbnailsLabels;

type ExternalProps = {
    labels: UploadUneditedImagesLabels;
    orderId: number;
};

type UploadUneditedImagesProps = {
    onUpload: (formData: FormData) => Promise<number[]>;
    imageData: ImageModel[];
    onDeleteFailedUpload: (photoUploadMetadataId: number) => void;
    setImageInProgress: (imageUploadResponse: ImageUploadResponse) => void;
    onDeleteSingleImage: (imageId: number, isUploadedByCustomer: boolean) => Promise<void>;
    onDeleteMultipleImages: (images: CustomerOrSupportImage[]) => void;
    onDownloadImages: () => Promise<any>;
    onUploadComplete: () => void;
    onRetryUpload: () => void;
} & ExternalProps;

export function UploadUneditedImages({
    labels,
    imageData,
    orderId,
    onUpload,
    onDeleteFailedUpload,
    onDeleteSingleImage,
    onDeleteMultipleImages,
    setImageInProgress,
    onUploadComplete,
    onRetryUpload,
    onDownloadImages
}: UploadUneditedImagesProps) {
    let formId = `unedited-${orderId}`;
    let baseUrl = window.appConfig.apiUrl;
    let formAction = `${baseUrl}/views/digital-styling-upload?orderId=${orderId}`;

    let {
        filesAdded,
        addFileToDropzone,
        removeFileFromDropzone,
        isDoneUploading,
        setIsDoneUploading
    } = useImageDataUpdate(imageData, onUploadComplete);

    function handleUpload(formData: FormData): Promise<number[]> {
        setIsDoneUploading(false);
        return onUpload(formData);
    }

    return (
        <>
            {imageData.map((data) => (
                <Box
                    key={data.id}
                    sx={{ display: 'flex', flexDirection: 'column', padding: '20px' }}
                >
                    {data.thumbnails.length > 0 ? (
                        <DigitalStylingThumbnails
                            files={data.thumbnails}
                            filesInProgress={data.uploadsInProgress}
                            onDelete={onDeleteSingleImage}
                            onDeleteMultiple={onDeleteMultipleImages}
                            enableActions={isDoneUploading}
                            onClickDownloadImages={onDownloadImages}
                            labels={labels}
                        />
                    ) : null}
                    {data.uploadsInProgress.length > 0 ? (
                        <UploadsInProgress
                            labels={{
                                heading: labels.uploadsInProgressHeading
                            }}
                            files={data.uploadsInProgress}
                        />
                    ) : null}
                    {data.failedUploads.length > 0 ? (
                        <FailedUploads
                            labels={{
                                heading: labels.failedUploadsHeading
                            }}
                            files={data.failedUploads}
                            onDelete={onDeleteFailedUpload}
                        />
                    ) : null}
                    <ImageDropzone
                        labels={labels}
                        filesAdded={filesAdded}
                        addFile={addFileToDropzone}
                        removeFile={removeFileFromDropzone}
                        orderId={orderId}
                        formId={formId}
                        formAction={formAction}
                        onUpload={handleUpload}
                        setImageInProgress={setImageInProgress}
                        onRetryUpload={onRetryUpload}
                    />
                    <Button
                        sx={{ alignSelf: 'flex-end' }}
                        variant="contained"
                        type="submit"
                        startIcon={<FileUploadIcon />}
                        disabled={filesAdded.length < 1}
                        form={formId}
                    >
                        {labels.uploadButton}
                    </Button>
                </Box>
            ))}
        </>
    );
}

export default function UploadUneditedImagesDataLayer({ labels, orderId }: ExternalProps) {
    let [refetchInterval, setRefetchInterval] = React.useState<number>(0);

    let queryKey = 'uneditedDigitalStylingImages';
    let { data, isError, error } = useQuery(
        [queryKey],
        () => UneditedImagesApi.getImages(orderId),
        {
            refetchInterval
        }
    );

    let queryClient = useQueryClient();

    let saveMetadataMutation = useMutation(
        ({ formData }: { formData: FormData }) => UneditedImagesApi.saveMetadata(formData),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKey);
            }
        }
    );

    let deleteFailedUploadMutation = useMutation(
        ({ photoUploadMetadataId }: { photoUploadMetadataId: number }) =>
            UneditedImagesApi.deleteFailedUploadImage(photoUploadMetadataId),
        {
            onSettled: () => {
                queryClient.invalidateQueries(queryKey);
            }
        }
    );

    let setImageInProgressMutation = useMutation(
        ({ imageUploadResponse }: { imageUploadResponse: ImageUploadResponse }) =>
            UneditedImagesApi.setImageInProgress(imageUploadResponse),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKey);
            }
        }
    );

    let deleteSingleImageMutation = useMutation(
        ({ imageId }: { imageId: number }) => UneditedImagesApi.deleteSingleImage(imageId),
        {
            onSettled: () => {
                queryClient.invalidateQueries(queryKey);
            }
        }
    );

    let deleteSingleCustomerImageMutation = useMutation(
        ({ imageId }: { imageId: number }) => UneditedImagesApi.deleteSingleCustomerImage(imageId),
        {
            onSettled: () => {
                queryClient.invalidateQueries(queryKey);
            }
        }
    );

    let deleteMultipleImagesMutation = useMutation(
        ({ images }: { images: CustomerOrSupportImage[] }) =>
            UneditedImagesApi.deleteMultipleImages(images),
        {
            onSettled: () => {
                queryClient.invalidateQueries(queryKey);
            }
        }
    );

    function handleDeleteSingleImage(
        imageId: number,
        isUploadedByCustomer: boolean
    ): Promise<void> {
        if (isUploadedByCustomer) {
            return deleteSingleCustomerImageMutation.mutateAsync({ imageId });
        }

        return deleteSingleImageMutation.mutateAsync({ imageId });
    }

    function handleDeleteMultipleImages(images: CustomerOrSupportImage[]): Promise<void> {
        return deleteMultipleImagesMutation.mutateAsync({ images });
    }

    function handleUpload(formData: FormData): Promise<number[]> {
        return saveMetadataMutation.mutateAsync({ formData });
    }

    function handleDeleteFailedUpload(photoUploadMetadataId: number): Promise<void> {
        return deleteFailedUploadMutation.mutateAsync({ photoUploadMetadataId });
    }

    function setImageInProgress(imageUploadResponse: ImageUploadResponse) {
        setRefetchInterval(IMAGE_REFETCH_INTERVAL);
        setImageInProgressMutation.mutate({ imageUploadResponse });
    }

    function handleDownloadImages(): Promise<any> {
        function downloadFunc() {
            return UneditedImagesApi.downloadImages(orderId);
        }

        return downloadZip(downloadFunc);
    }

    if (isError) {
        console.error('error fetching image data', error);
    }

    let imageData: ImageModel[] = data ? data : [];

    return (
        <UploadUneditedImages
            labels={labels}
            orderId={orderId}
            onUpload={handleUpload}
            onDeleteSingleImage={handleDeleteSingleImage}
            onDeleteMultipleImages={handleDeleteMultipleImages}
            onDeleteFailedUpload={handleDeleteFailedUpload}
            imageData={imageData}
            setImageInProgress={setImageInProgress}
            onUploadComplete={() => {
                setRefetchInterval(0);
            }}
            onRetryUpload={() => {
                if (refetchInterval === 0) {
                    setRefetchInterval(IMAGE_REFETCH_INTERVAL);
                }
            }}
            onDownloadImages={handleDownloadImages}
        />
    );
}
